import { useQuery } from '@tanstack/react-query';
import { getPublicClient } from '@wagmi/core';
import { wagmiConfig } from 'structure/providers/wagmiConfig';
import { LegacyPositionRow } from 'structure/capital-manager/constants/VaultsLegacy';
import legacyStakingAbi from 'utils/constants/abi/SDAOTokenStaking';
import legacyBondedAbi from '@singularitydao/v1-farming-contracts/abi/SDAOBondedTokenStake.json';
import { Abi, erc20Abi } from 'viem';

export type LegacyPositionBalance = {
  balance: bigint;
  pending: bigint;
  tokenAddress?: `0x${string}`;
  tokenSymbol?: string;
  tokenDecimals: number;
};

export type LegacyPositionBalancesMap = Record<string, LegacyPositionBalance>;

type LegacyCallType = 'token-address' | 'user-info' | 'pending';

type LegacyCall = {
  positionId: string;
  category: LegacyPositionRow['category'];
  type: LegacyCallType;
  contract: {
    address: `0x${string}`;
    abi: Abi;
    functionName: string;
    chainId: number;
    args?: readonly unknown[];
  };
};

const groupByChainId = (positions: LegacyPositionRow[]) => {
  const map = new Map<number, LegacyPositionRow[]>();
  for (const position of positions) {
    const group = map.get(position.chainId) || [];
    group.push(position);
    map.set(position.chainId, group);
  }
  return map;
};

const createEmptyBalance = (): LegacyPositionBalance => ({
  balance: BigInt(0),
  pending: BigInt(0),
  tokenDecimals: 18,
});

const parseAddress = (value: unknown): `0x${string}` | undefined => {
  if (typeof value !== 'string') return undefined;
  if (!value.startsWith('0x')) return undefined;
  return value as `0x${string}`;
};

const parseBigInt = (value: unknown): bigint => {
  if (typeof value === 'bigint') return value;
  return BigInt(0);
};

const parseTupleBigInt = (value: unknown, idx: number): bigint => {
  if (!Array.isArray(value)) return BigInt(0);
  return parseBigInt(value[idx]);
};

const parseDecimals = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'bigint') return Number(value);
  return 18;
};

const buildPrimaryCalls = (props: {
  positions: LegacyPositionRow[];
  userAddress: `0x${string}`;
}) => {
  const { positions, userAddress } = props;
  return positions.flatMap((position) => {
    if (position.category === 'BONDED') {
      return [
        {
          positionId: position.id,
          category: position.category,
          type: 'token-address',
          contract: {
            address: position.poolAddress as `0x${string}`,
            abi: legacyBondedAbi as Abi,
            functionName: 'token',
            chainId: position.chainId,
          },
        },
        {
          positionId: position.id,
          category: position.category,
          type: 'user-info',
          contract: {
            address: position.poolAddress as `0x${string}`,
            abi: legacyBondedAbi as Abi,
            functionName: 'getStakeInfo',
            args: [userAddress],
            chainId: position.chainId,
          },
        },
      ] as LegacyCall[];
    }

    return [
      {
        positionId: position.id,
        category: position.category,
        type: 'token-address',
        contract: {
          address: position.poolAddress as `0x${string}`,
          abi: legacyStakingAbi,
          functionName: 'lpToken',
          args: [BigInt(position.poolId)],
          chainId: position.chainId,
        },
      },
      {
        positionId: position.id,
        category: position.category,
        type: 'user-info',
        contract: {
          address: position.poolAddress as `0x${string}`,
          abi: legacyStakingAbi,
          functionName: 'userInfo',
          args: [BigInt(position.poolId), userAddress],
          chainId: position.chainId,
        },
      },
      {
        positionId: position.id,
        category: position.category,
        type: 'pending',
        contract: {
          address: position.poolAddress as `0x${string}`,
          abi: legacyStakingAbi,
          functionName: 'pendingRewards',
          args: [BigInt(position.poolId), userAddress],
          chainId: position.chainId,
        },
      },
    ] as LegacyCall[];
  });
};

const buildTokenMetadataCalls = (props: {
  chainId: number;
  tokenAddresses: `0x${string}`[];
}) => {
  const { chainId, tokenAddresses } = props;

  return tokenAddresses.flatMap((tokenAddress) => [
    {
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'decimals',
      chainId,
    },
    {
      address: tokenAddress,
      abi: erc20Abi,
      functionName: 'symbol',
      chainId,
    },
  ]);
};

export const useLegacyPositionsBalances = (props: {
  legacyPositions: LegacyPositionRow[];
  userAddress?: `0x${string}`;
}) => {
  const { legacyPositions, userAddress } = props;

  return useQuery({
    queryKey: ['legacy-positions-balances', userAddress],
    enabled: !!userAddress,
    staleTime: 30_000,
    queryFn: async (): Promise<LegacyPositionBalancesMap> => {
      if (!userAddress) {
        return {};
      }

      const balanceMap = legacyPositions.reduce((acc, position) => {
        acc[position.id] = createEmptyBalance();
        return acc;
      }, {} as LegacyPositionBalancesMap);

      const positionsByChain = groupByChainId(legacyPositions);

      for (const [chainId, positions] of positionsByChain) {
        const publicClient = getPublicClient(wagmiConfig, { chainId });
        if (!publicClient) {
          continue;
        }

        const primaryCalls = buildPrimaryCalls({
          positions,
          userAddress,
        });

        if (!primaryCalls.length) {
          continue;
        }

        const primaryResults = await publicClient.multicall({
          contracts: primaryCalls.map((call) => call.contract),
          allowFailure: true,
          authorizationList: undefined,
        });

        for (let i = 0; i < primaryCalls.length; i++) {
          const call = primaryCalls[i];
          const result = primaryResults[i];
          const current = balanceMap[call.positionId] || createEmptyBalance();

          if (!result || result.status !== 'success') {
            balanceMap[call.positionId] = current;
            continue;
          }

          if (call.type === 'token-address') {
            const tokenAddress = parseAddress(result.result);
            if (tokenAddress) {
              current.tokenAddress = tokenAddress;
            }
            balanceMap[call.positionId] = current;
            continue;
          }

          if (call.type === 'user-info') {
            if (call.category === 'BONDED') {
              // getStakeInfo => [found, amount, rewardComputeIndex, bonusAmount]
              current.balance = parseTupleBigInt(result.result, 1);
              current.pending = parseTupleBigInt(result.result, 3);
            } else {
              // userInfo => [amount, rewardDebt]
              current.balance = parseTupleBigInt(result.result, 0);
            }
            balanceMap[call.positionId] = current;
            continue;
          }

          if (call.type === 'pending') {
            current.pending = parseBigInt(result.result);
            balanceMap[call.positionId] = current;
          }
        }

        const tokenAddresses = Array.from(
          new Set(
            positions
              .map((position) => balanceMap[position.id]?.tokenAddress)
              .filter((tokenAddress): tokenAddress is `0x${string}` =>
                Boolean(tokenAddress)
              )
          )
        );

        if (!tokenAddresses.length) {
          continue;
        }

        const tokenCalls = buildTokenMetadataCalls({
          chainId,
          tokenAddresses,
        });

        const tokenResults = await publicClient.multicall({
          contracts: tokenCalls,
          allowFailure: true,
          authorizationList: undefined,
        });

        const tokenMetadata = tokenAddresses.reduce(
          (acc, tokenAddress, idx) => {
            const decimalsResult = tokenResults[idx * 2];
            const symbolResult = tokenResults[idx * 2 + 1];

            acc[tokenAddress.toLowerCase()] = {
              tokenDecimals:
                decimalsResult?.status === 'success'
                  ? parseDecimals(decimalsResult.result)
                  : 18,
              tokenSymbol:
                symbolResult?.status === 'success' &&
                typeof symbolResult.result === 'string'
                  ? symbolResult.result
                  : undefined,
            };

            return acc;
          },
          {} as Record<
            string,
            {
              tokenDecimals: number;
              tokenSymbol?: string;
            }
          >
        );

        for (const position of positions) {
          const current = balanceMap[position.id];
          if (!current?.tokenAddress) {
            continue;
          }

          const metadata = tokenMetadata[current.tokenAddress.toLowerCase()];
          if (!metadata) {
            continue;
          }

          balanceMap[position.id] = {
            ...current,
            tokenDecimals: metadata.tokenDecimals,
            tokenSymbol: metadata.tokenSymbol,
          };
        }
      }

      return balanceMap;
    },
  });
};
